// client/src/components/common/StorefrontNavbar.js
// Enterprise Feature: Branded navbar for wholesaler storefront URLs
// Replaces the standard Navbar when a business enters via custom slug URL
// Creates a "this is MY platform" feel for the wholesaler

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, ClipboardList, LogOut, User, Menu, X, Heart, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from './LanguageSwitcher';
import { getImageUrl } from '../../utils/imageHelper';

const StorefrontNavbar = ({ wholesaler, wholesalerId, slug }) => {
  const { t } = useTranslation(['business', 'common']);
  const navigate = useNavigate();
  const { items: cartItems } = useCart();
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const cartCount = cartItems?.length || 0;
  const companyName = wholesaler?.companyName || '';
  const logo = wholesaler?.logo;
  
  // Always use the slug for storefront URLs (slug prop is the raw URL param)
  const storefrontSlug = slug || wholesaler?.customSlug || wholesalerId;
  const storefrontUrl = `/wholesalers/${storefrontSlug}`;
  // sessionStorage carries the storefront context — no need for query params

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* ─── Left: Wholesaler Brand ─── */}
            <Link
              to={storefrontUrl}
              className="flex items-center gap-3 min-w-0"
            >
              {logo ? (
                <img
                  src={getImageUrl(logo)}
                  alt={companyName}
                  className="w-9 h-9 rounded-lg object-contain bg-gray-50 border border-gray-100"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                  {companyName.charAt(0)}
                </div>
              )}
              <span className="text-lg font-bold text-gray-900 truncate max-w-[200px]">
                {companyName}
              </span>
            </Link>

            {/* ─── Center: Navigation Links (desktop) ─── */}
            <div className="hidden md:flex items-center gap-1">
              <Link
                to={storefrontUrl}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Package className="w-4 h-4 inline mr-1.5" />
                {t('catalog.title', 'Catalog')}
              </Link>
              {isAuthenticated && user?.role === 'business' && (
                <>
                  <Link
                    to={`/business/orders`}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <ClipboardList className="w-4 h-4 inline mr-1.5" />
                    {t('common:common.myOrders', 'My Orders')}
                  </Link>
                  <Link
                    to={`/favorites`}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Heart className="w-4 h-4 inline mr-1.5" />
                    {t('common:common.favorites', 'Favorites')}
                  </Link>
                </>
              )}
            </div>

            {/* ─── Right: Cart + User ─── */}
            <div className="flex items-center gap-2">
              <LanguageSwitcher />

              {isAuthenticated && user?.role === 'business' && (
                <>
                  <NotificationBell />
                  <Link
                    to={`/cart`}
                    className="relative p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {cartCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {cartCount > 9 ? '9+' : cartCount}
                      </span>
                    )}
                  </Link>
                </>
              )}

              {isAuthenticated ? (
                <div className="hidden md:flex items-center gap-2">
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title={user?.name || ''}
                  >
                    <User className="w-4 h-4" />
                    <span className="max-w-[100px] truncate">{user?.name || t('common:common.profile', 'Profile')}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title={t('common:common.logout', 'Logout')}
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('common:common.login', 'Login')}
                </Link>
              )}

              {/* Mobile menu toggle */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* ─── Mobile dropdown ─── */}
        {mobileOpen && (
          <div className="md:hidden border-t bg-white pb-4">
            <div className="px-4 py-3 space-y-1">
              <Link
                to={storefrontUrl}
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-2.5 text-gray-700 hover:bg-blue-50 rounded-lg font-medium"
              >
                📦 {t('catalog.title', 'Catalog')}
              </Link>
              {isAuthenticated && user?.role === 'business' && (
                <>
                  <Link to={`/business/orders`} onClick={() => setMobileOpen(false)}
                    className="block px-4 py-2.5 text-gray-700 hover:bg-blue-50 rounded-lg">
                    📋 {t('common:common.myOrders', 'My Orders')}
                  </Link>
                  <Link to={`/favorites`} onClick={() => setMobileOpen(false)}
                    className="block px-4 py-2.5 text-gray-700 hover:bg-blue-50 rounded-lg">
                    ❤️ {t('common:common.favorites', 'Favorites')}
                  </Link>
                  <Link to={`/cart`} onClick={() => setMobileOpen(false)}
                    className="block px-4 py-2.5 text-gray-700 hover:bg-blue-50 rounded-lg">
                    🛒 {t('common:common.cart', 'Cart')} {cartCount > 0 && `(${cartCount})`}
                  </Link>
                </>
              )}
              {isAuthenticated && (
                <button onClick={() => { handleLogout(); setMobileOpen(false); }}
                  className="block w-full text-left px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg">
                  {t('common:common.logout', 'Logout')}
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* ─── Powered by WholesaleHub footer badge ─── */}
      {/* This is rendered as a subtle bottom-right badge on the storefront pages */}
    </>
  );
};

export default StorefrontNavbar;